import React, { useState, useEffect } from 'react';
import { FiSearch, FiX, FiPlus, FiEdit, FiTrash2, FiFilter } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import exerciseLibraryService from '../../services/exerciseLibrary';
import './ExerciseLibraryBrowser.css';

const ExerciseLibraryBrowser = ({ onSelect, onClose }) => {
  const { currentUser, userProfile } = useAuth();

  const [exercises, setExercises] = useState([]);
  const [filteredExercises, setFilteredExercises] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSubcategory, setSelectedSubcategory] = useState('all');
  const [selectedEquipment, setSelectedEquipment] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showCustomExerciseForm, setShowCustomExerciseForm] = useState(false);
  const [loading, setLoading] = useState(true);

  // Custom exercise form
  const [customExercise, setCustomExercise] = useState({
    name: '',
    equipment: 'barbell',
    category: 'resistance',
    subcategory: 'back',
    primaryMuscles: [],
    secondaryMuscles: [],
    measurementType: 'sets_reps',
    defaultSets: 3,
    defaultReps: '10',
    defaultRestSeconds: 60,
    difficulty: 'intermediate',
    notes: ''
  });

  useEffect(() => {
    loadExercises();
  }, [currentUser]);

  useEffect(() => {
    applyFilters();
  }, [exercises, searchTerm, selectedCategory, selectedSubcategory, selectedEquipment, selectedDifficulty]);

  const loadExercises = async () => {
    setLoading(true);
    try {
      const allExercises = await exerciseLibraryService.getAllExercises();
      setExercises(allExercises);
    } catch (error) {
      console.error('[ExerciseLibraryBrowser] Error loading exercises:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    const filtered = exerciseLibraryService.searchExercises(exercises, searchTerm, {
      category: selectedCategory,
      subcategory: selectedSubcategory,
      equipment: selectedEquipment,
      difficulty: selectedDifficulty
    });
    setFilteredExercises(filtered);
  };

  const handleCreateCustomExercise = async () => {
    if (!customExercise.name) {
      alert('Please enter an exercise name');
      return;
    }

    if (customExercise.primaryMuscles.length === 0) {
      alert('Please select at least one primary muscle group');
      return;
    }

    try {
      const creatorName = userProfile?.displayName || userProfile?.email || 'Unknown Coach';
      const newExercise = await exerciseLibraryService.createCustomExercise(
        currentUser.uid,
        customExercise,
        creatorName
      );

      setExercises([...exercises, newExercise]);
      setShowCustomExerciseForm(false);
      resetCustomExerciseForm();
      alert('Custom exercise created successfully and is now available to all coaches!');
    } catch (error) {
      console.error('[ExerciseLibraryBrowser] Error creating custom exercise:', error);
      alert('Error creating custom exercise');
    }
  };

  const handleDeleteCustomExercise = async (exerciseId) => {
    if (!window.confirm('Delete this custom exercise?')) return;

    try {
      await exerciseLibraryService.deleteCustomExercise(exerciseId);
      setExercises(exercises.filter(ex => ex.id !== exerciseId));
      alert('Exercise deleted successfully');
    } catch (error) {
      console.error('[ExerciseLibraryBrowser] Error deleting exercise:', error);
      alert('Error deleting exercise');
    }
  };

  const resetCustomExerciseForm = () => {
    setCustomExercise({
      name: '',
      equipment: 'barbell',
      category: 'resistance',
      subcategory: 'back',
      primaryMuscles: [],
      secondaryMuscles: [],
      measurementType: 'sets_reps',
      defaultSets: 3,
      defaultReps: '10',
      defaultRestSeconds: 60,
      difficulty: 'intermediate',
      notes: ''
    });
  };

  // Muscle groups available for selection
  const muscleGroups = [
    { id: 'pectorals', name: 'Pectorals (Chest)' },
    { id: 'upper_pectorals', name: 'Upper Pectorals' },
    { id: 'lower_pectorals', name: 'Lower Pectorals' },
    { id: 'inner_pectorals', name: 'Inner Pectorals' },
    { id: 'lats', name: 'Lats (Back)' },
    { id: 'rhomboids', name: 'Rhomboids' },
    { id: 'traps', name: 'Traps' },
    { id: 'middle_traps', name: 'Middle Traps' },
    { id: 'erector_spinae', name: 'Erector Spinae (Lower Back)' },
    { id: 'front_delts', name: 'Front Deltoids' },
    { id: 'middle_delts', name: 'Middle Deltoids' },
    { id: 'rear_delts', name: 'Rear Deltoids' },
    { id: 'shoulders', name: 'Shoulders (General)' },
    { id: 'rotator_cuff', name: 'Rotator Cuff' },
    { id: 'biceps', name: 'Biceps' },
    { id: 'brachialis', name: 'Brachialis' },
    { id: 'triceps', name: 'Triceps' },
    { id: 'triceps_long_head', name: 'Triceps Long Head' },
    { id: 'triceps_lateral_head', name: 'Triceps Lateral Head' },
    { id: 'triceps_medial_head', name: 'Triceps Medial Head' },
    { id: 'forearms', name: 'Forearms' },
    { id: 'quadriceps', name: 'Quadriceps' },
    { id: 'hamstrings', name: 'Hamstrings' },
    { id: 'glutes', name: 'Glutes' },
    { id: 'calves', name: 'Calves' },
    { id: 'hip_flexors', name: 'Hip Flexors' },
    { id: 'core', name: 'Core (General)' },
    { id: 'rectus_abdominis', name: 'Rectus Abdominis (Abs)' },
    { id: 'lower_abs', name: 'Lower Abs' },
    { id: 'obliques', name: 'Obliques' },
    { id: 'transverse_abdominis', name: 'Transverse Abdominis' },
    { id: 'cardiovascular', name: 'Cardiovascular' },
    { id: 'full_body', name: 'Full Body' },
    { id: 'upper_body', name: 'Upper Body' },
    { id: 'legs', name: 'Legs (General)' },
    { id: 'arms', name: 'Arms (General)' }
  ];

  // Toggle muscle in primary muscles array
  const togglePrimaryMuscle = (muscleId) => {
    setCustomExercise(prev => {
      const muscles = prev.primaryMuscles.includes(muscleId)
        ? prev.primaryMuscles.filter(m => m !== muscleId)
        : [...prev.primaryMuscles, muscleId];
      return { ...prev, primaryMuscles: muscles };
    });
  };

  // Toggle muscle in secondary muscles array
  const toggleSecondaryMuscle = (muscleId) => {
    setCustomExercise(prev => {
      const muscles = prev.secondaryMuscles.includes(muscleId)
        ? prev.secondaryMuscles.filter(m => m !== muscleId)
        : [...prev.secondaryMuscles, muscleId];
      return { ...prev, secondaryMuscles: muscles };
    });
  };

  const categories = exerciseLibraryService.getCategories();
  const subcategories = exerciseLibraryService.getSubcategories(selectedCategory);
  const equipmentTypes = exerciseLibraryService.getEquipmentTypes();
  const difficultyLevels = exerciseLibraryService.getDifficultyLevels();

  return (
    <div className="exercise-library-modal">
      <div className="modal-overlay" onClick={onClose}></div>

      <div className="modal-content">
        {/* Header */}
        <div className="modal-header">
          <h2>Exercise Library</h2>
          <div className="header-actions">
            <button
              className="create-custom-btn"
              onClick={() => setShowCustomExerciseForm(!showCustomExerciseForm)}
            >
              <FiPlus /> Create Custom Exercise
            </button>
            <button className="close-modal-btn" onClick={onClose}>
              <FiX />
            </button>
          </div>
        </div>

        {/* Custom Exercise Form */}
        {showCustomExerciseForm && (
          <div className="custom-exercise-form">
            <h3>Create Custom Exercise</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Exercise Name *</label>
                <input
                  type="text"
                  value={customExercise.name}
                  onChange={(e) => setCustomExercise({ ...customExercise, name: e.target.value })}
                  placeholder="e.g., Bulgarian Split Squat"
                />
              </div>

              <div className="form-group">
                <label>Equipment</label>
                <select
                  value={customExercise.equipment}
                  onChange={(e) => setCustomExercise({ ...customExercise, equipment: e.target.value })}
                >
                  {equipmentTypes.filter(e => e.id !== 'all').map(eq => (
                    <option key={eq.id} value={eq.id}>{eq.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Category</label>
                <select
                  value={customExercise.category}
                  onChange={(e) => setCustomExercise({ ...customExercise, category: e.target.value })}
                >
                  {categories.filter(c => c.id !== 'all').map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Difficulty</label>
                <select
                  value={customExercise.difficulty}
                  onChange={(e) => setCustomExercise({ ...customExercise, difficulty: e.target.value })}
                >
                  {difficultyLevels.filter(d => d.id !== 'all').map(diff => (
                    <option key={diff.id} value={diff.id}>{diff.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group full-width">
                <label>Muscles Worked *</label>
                <div className="muscle-selector-container">
                  <div className="muscle-chips">
                    {customExercise.primaryMuscles.length === 0 ? (
                      <span className="no-selection">No muscles selected</span>
                    ) : (
                      customExercise.primaryMuscles.map(muscleId => {
                        const muscle = muscleGroups.find(m => m.id === muscleId);
                        return (
                          <span key={muscleId} className="muscle-chip primary">
                            {muscle?.name || muscleId}
                            <button
                              type="button"
                              onClick={() => togglePrimaryMuscle(muscleId)}
                              className="remove-muscle"
                            >
                              <FiX />
                            </button>
                          </span>
                        );
                      })
                    )}
                  </div>
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        togglePrimaryMuscle(e.target.value);
                        e.target.value = '';
                      }
                    }}
                    value=""
                    className="muscle-dropdown"
                  >
                    <option value="">+ Add Muscle Group</option>
                    {muscleGroups
                      .filter(m => !customExercise.primaryMuscles.includes(m.id))
                      .map(muscle => (
                        <option key={muscle.id} value={muscle.id}>{muscle.name}</option>
                      ))
                    }
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Measurement Type</label>
                <select
                  value={customExercise.measurementType}
                  onChange={(e) => setCustomExercise({ ...customExercise, measurementType: e.target.value })}
                >
                  <option value="sets_reps">Sets & Reps</option>
                  <option value="time">Time-Based</option>
                  <option value="distance">Distance</option>
                  <option value="intervals">Intervals</option>
                </select>
              </div>

              <div className="form-group">
                <label>Default Sets</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={customExercise.defaultSets}
                  onChange={(e) => setCustomExercise({ ...customExercise, defaultSets: parseInt(e.target.value) })}
                />
              </div>

              <div className="form-group">
                <label>Default Reps</label>
                <input
                  type="text"
                  value={customExercise.defaultReps}
                  onChange={(e) => setCustomExercise({ ...customExercise, defaultReps: e.target.value })}
                  placeholder="e.g., 8-12"
                />
              </div>

              <div className="form-group">
                <label>Default Rest (seconds)</label>
                <input
                  type="number"
                  min="0"
                  max="300"
                  value={customExercise.defaultRestSeconds}
                  onChange={(e) => setCustomExercise({ ...customExercise, defaultRestSeconds: parseInt(e.target.value) })}
                />
              </div>

              <div className="form-group full-width">
                <label>Notes</label>
                <textarea
                  value={customExercise.notes}
                  onChange={(e) => setCustomExercise({ ...customExercise, notes: e.target.value })}
                  placeholder="Add any notes about form, cues, or variations..."
                  rows="3"
                />
              </div>
            </div>

            <div className="form-actions">
              <button className="cancel-btn" onClick={() => {
                setShowCustomExerciseForm(false);
                resetCustomExerciseForm();
              }}>
                Cancel
              </button>
              <button className="create-btn" onClick={handleCreateCustomExercise}>
                <FiPlus /> Create Exercise
              </button>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="search-filters-section">
          <div className="search-bar">
            <FiSearch />
            <input
              type="text"
              placeholder="Search exercises by name, muscle group, or equipment..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <button
            className="toggle-filters-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FiFilter /> {showFilters ? 'Hide' : 'Show'} Filters
          </button>
        </div>

        {showFilters && (
          <div className="filters-panel">
            <div className="filter-group">
              <label>Category</label>
              <select value={selectedCategory} onChange={(e) => {
                setSelectedCategory(e.target.value);
                setSelectedSubcategory('all');
              }}>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Subcategory</label>
              <select value={selectedSubcategory} onChange={(e) => setSelectedSubcategory(e.target.value)}>
                {subcategories.map(sub => (
                  <option key={sub.id} value={sub.id}>{sub.name}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Equipment</label>
              <select value={selectedEquipment} onChange={(e) => setSelectedEquipment(e.target.value)}>
                {equipmentTypes.map(eq => (
                  <option key={eq.id} value={eq.id}>{eq.name}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Difficulty</label>
              <select value={selectedDifficulty} onChange={(e) => setSelectedDifficulty(e.target.value)}>
                {difficultyLevels.map(diff => (
                  <option key={diff.id} value={diff.id}>{diff.name}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Exercise List */}
        <div className="exercises-list">
          {loading ? (
            <div className="loading">Loading exercises...</div>
          ) : filteredExercises.length === 0 ? (
            <div className="no-results">
              <p>No exercises found</p>
              <p>Try adjusting your filters or create a custom exercise</p>
            </div>
          ) : (
            filteredExercises.map(exercise => (
              <div key={exercise.id} className="exercise-item">
                <div className="exercise-info">
                  <div className="exercise-name">
                    {exercise.name}
                    {exercise.isCustom && (
                      <span className="custom-badge" title={`Created by ${exercise.creatorName || 'Unknown'}`}>
                        Custom
                      </span>
                    )}
                  </div>
                  <div className="exercise-details">
                    <span className="equipment">{exercise.equipment}</span>
                    <span className="category">{exercise.subcategory || exercise.category}</span>
                    <span className="difficulty">{exercise.difficulty}</span>
                    <span className="defaults">
                      {exercise.defaultSets} × {exercise.defaultReps} | {exercise.defaultRestSeconds}s rest
                    </span>
                    {exercise.isCustom && exercise.creatorName && (
                      <span className="creator-name">by {exercise.creatorName}</span>
                    )}
                  </div>
                  {exercise.primaryMuscles && exercise.primaryMuscles.length > 0 && (
                    <div className="muscles">
                      <strong>Targets:</strong> {exercise.primaryMuscles.join(', ')}
                    </div>
                  )}
                </div>
                <div className="exercise-actions">
                  {exercise.isCustom && exercise.createdBy === currentUser.uid && (
                    <button
                      className="delete-exercise-btn"
                      onClick={() => handleDeleteCustomExercise(exercise.id)}
                      title="Delete custom exercise"
                    >
                      <FiTrash2 />
                    </button>
                  )}
                  <button
                    className="select-exercise-btn"
                    onClick={() => onSelect(exercise)}
                  >
                    <FiPlus /> Add
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="results-count">
          Showing {filteredExercises.length} of {exercises.length} exercises
        </div>
      </div>
    </div>
  );
};

export default ExerciseLibraryBrowser;
